import { useEffect, useState } from 'react';
import { Grid } from '~/engine/data/common/Grid';
import { assembleVolumeFromSlices } from '~/domain/services/assembleVolume';
import { autoWindowFromVolume } from '~/domain/services/percentileWindow';
import {
    ReformatTarget,
    reformatTargetModality,
    sourcePaddingValue,
} from '~/domain/services/imageService';

export interface ReformatSourceVolume {
    pixels: Int16Array;
    grid: Grid;
    /** What the reformat will pad with — measured once, here. */
    paddingValue: number;
    /** Display window for the preview, as [centre, width]. */
    window: [number, number];
    /**
     * Extent of the source that holds a duplicated neighbour rather than
     * an acquired slice, mm. Zero for a regularly spaced series.
     */
    gapFilledMm: number;
}

/**
 * The source voxels, materialised once per target.
 *
 * The preview resamples a single output slice out of this, and the reformat
 * resamples the whole output out of the same thing, so the picture the user
 * accepts is made from the data the reformat will use. Assembling it twice
 * would also mean holding two copies of a volume that can run to hundreds
 * of megabytes.
 *
 * Deferred to an idle callback rather than computed while the dialog is
 * opening: a raw series has to be stacked slice by slice, and the scouts
 * are what the user looks at first.
 */
export default function useReformatSourceVolume(
    target: ReformatTarget,
    enabled: boolean
): { volume: ReformatSourceVolume | null; failed: boolean } {
    const [volume, setVolume] = useState<ReformatSourceVolume | null>(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        setVolume(null);
        setFailed(false);
        if (!enabled) {
            return;
        }

        let cancelled = false;
        const handle = requestIdleCallback(() => {
            if (cancelled) {
                return;
            }
            try {
                const assembled =
                    target.kind === 'rawSeries'
                        ? assembleVolumeFromSlices(target.series)
                        : {
                              pixels: target.image.pixelData,
                              grid: target.image.grid,
                              gapFilledMm: 0,
                          };
                const { pixels, grid } = assembled;
                if (!cancelled) {
                    setVolume({
                        pixels,
                        grid,
                        paddingValue: sourcePaddingValue(
                            pixels,
                            reformatTargetModality(target)
                        ),
                        window: autoWindowFromVolume(pixels),
                        gapFilledMm: assembled.gapFilledMm,
                    });
                }
            } catch (e) {
                // The preview is an aid, not the operation: a source the
                // reformat itself would refuse must not take the dialog
                // down before the user reaches the refusal. It is reported
                // rather than swallowed, though — a preview that silently
                // never appears reads as one still loading, and the user
                // would accept the operation having seen nothing.
                //
                // Only the error's type: what is raised below here names
                // slices by UID, and a UID in a console is PHI.
                console.error(
                    'Could not read the source volume:',
                    e instanceof Error ? e.name : typeof e
                );
                if (!cancelled) {
                    setFailed(true);
                }
            }
        });

        return () => {
            cancelled = true;
            cancelIdleCallback(handle);
        };
    }, [target, enabled]);

    return { volume, failed };
}
